<template>
    <div class="flex flex-col h-full" :class="isSplitterDragging ? 'pointer-events-none' : ''" @drop.prevent.stop="() => {}">
        <USkeleton v-if="isLoading" class="h-560 w-full"/>
        <div v-else-if="!documentMetaEnriched">
            ingen dokument å vise
        </div>

        <pdf-renderer
            v-else-if="isPdf"
            :key="documentMetaEnriched.uuid"
            :document-meta="documentMetaEnriched"
            @on-click-download="handleDownload"
        />
        <image-renderer
            v-else-if="isImage"
            :key="documentMetaEnriched.uuid"
            :document-meta="documentMetaEnriched"
            @on-click-download="handleDownload"
        />
        <object-renderer
            v-else
            :key="documentMetaEnriched.uuid"
            :document-meta="documentMetaEnriched"
            @on-click-download="handleDownload"
        />
    </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue'
import {storageDownloadFile, storageResolveUrl} from '@/services/document.service'
import {DocumentMeta, EnrichedDocument} from '@/types/transaction/DocumentMeta'
import {isSplitterDragging} from '@tritt/multipanel'
import {notifyError} from '@/services/infrastructure/notification.service'
import {getDocumentMeta} from '@/services/infrastructure/storage.service'
import PdfRenderer from '@/document/preview-renderers/PdfRenderer.vue'
import ImageRenderer from '@/document/preview-renderers/ImageRenderer.vue'
import ObjectRenderer from '@/document/preview-renderers/ObjectRenderer.vue'

const props = defineProps({
    documentMetaUuid: {
        type: String,
        required: true
    }
})

const emit = defineEmits<{ (e: 'documentLoaded', documentMeta: DocumentMeta): void }>()

const isLoading = ref(false)
const documentMetaEnriched = ref<EnrichedDocument>()
let loadSeq = 0

const isPdf = computed(() => documentMetaEnriched.value?.contentType?.includes('pdf'))
const isImage = computed(() => documentMetaEnriched.value?.contentType?.includes('image'))

const loadDocument = async () => {
    const seq = ++loadSeq
    // dont get the download url if ehfContent is provided
    try {
        isLoading.value = true
        const documentMeta = await getDocumentMeta(props.documentMetaUuid)
        if (seq !== loadSeq) return
        if (documentMeta) {
            const docUrl = await storageResolveUrl(documentMeta)
            if (seq !== loadSeq) return
            documentMetaEnriched.value = {...documentMeta, url: docUrl}
            emit('documentLoaded', documentMeta)
        }
    } catch (error) {
        if (seq !== loadSeq) return
        notifyError({title: 'Feil', description: {descriptionMessage: 'Klarte ikke å hente fil', error}})
    } finally {
        if (seq === loadSeq) {
            isLoading.value = false
        }
    }
}

const handleDownload = async () => {
    if (!documentMetaEnriched.value) {
        return
    }
    await storageDownloadFile(documentMetaEnriched.value)
}

onMounted(loadDocument)
watch(() => props.documentMetaUuid, loadDocument)

</script>

<style>

</style>